import React from 'react';
import cn from 'classnames';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { Link, useHistory } from 'react-router-dom';
import { useAuthUser } from 'react-auth-kit';

import {
  AdminSettingsSection, Button, Loader, PageBody, PageTitle, Table,
} from 'src/components';
import {
  usePlansQuery,
  useSubscriptionQuery,
} from 'src/api/queries/subscriptionQueries';
import { TCatalogPlan, TPlanQuota } from 'src/api/functions/subscriptionApi';
import { TSubscription } from 'src/types/TSubscription';
import { formatBillingBytes, formatNumberIntl } from 'src/utils/common';
import { useStripeCheckout } from 'src/hooks/useStripeCheckout';
import useModal from 'src/hooks/useModal';
import ChangePlanModal from './ChangePlanModal';
import CheckoutModal from './CheckoutModal';
import { BILLING_BASELINE_KEY, SUBSCRIPTION_ROUTE } from './Subscription';
import {
  ENTERPRISE_CONTACT_URL,
  hiddenCurrentPlan,
  isSelfServePlan,
  offeredPlans,
} from './planVisibility';
import messages from './messages';
import styles from './styles.module.scss';

/**
 * The plan picker — /admin/subscription/plans (Stripe-style: choosing a plan
 * is its own SURFACE, reached from one "Choose a plan" / "Change plan" CTA,
 * not a section competing with the subscription page).
 *
 * Layout: what every plan shares, stated once ("All plans include") → one CARD
 * per purchasable tier carrying only its differences (price, allowances, the
 * gated features it adds over the tier below) → the full tier×feature matrix
 * folded at the bottom for whoever wants the grid.
 *
 * Everything is derived from GET /api/v1/billing/plans — prices, allowances,
 * the shared-feature list and each card's "plus …" delta all come from the
 * catalog's quotas/addons, so a catalog change reaches this page with no
 * frontend release.
 */

// Every addon code the picker can name, with its customer-facing label. The
// gated codes drive the comparison matrix rows AND the per-card deltas; the
// always-on AI ops only ever appear in the shared "All plans include" line
// (they are on every tier, so they can never be a delta).
const FEATURE_LABELS: Record<string, keyof typeof messages> = {
  SHARE_ANALYTICS: 'featureShareAnalytics',
  CAMPAIGN_LINKS: 'featureCampaignLinks',
  DELIVERY_INSIGHTS: 'featureDeliveryInsights',
  ANALYTICS_INTEGRATIONS: 'featureAnalyticsIntegrations',
  AI_SCHEDULES: 'featureAiSchedules',
  FACE_RECOGNITION: 'featureFaceRecognition',
  BYO_STORAGE: 'featureByoStorage',
  DATABASE_EXPORT: 'featureDatabaseExport',
  REMOVE_BACKGROUND: 'featureRemoveBackground',
  IMAGE_ANALYSIS: 'featureImageAnalysis',
  AI_IMAGE_EDIT: 'featureAiImageEdit',
  PRINT_EXPORT: 'featurePrintExport',
};

// Capabilities every tier has as part of the PRODUCT, not as an entitlement:
// asset management and schemas, CDN delivery, and the AI Agent / MCP surface.
// They carry no `plan_addon_defaults` code, so — unlike everything above —
// they cannot be derived from the catalog and are named here.
//
// The trade-off is that this list cannot notice if one of them ever becomes
// gated. If any of these acquires an addon code, delete it here and add it to
// FEATURE_LABELS instead, so the shared line goes back to being computed.
const BASELINE_FEATURES: (keyof typeof messages)[] = [
  'featureCdn',
  'featureDamSchema',
  'featureAiAgentMcp',
];

// `<link>` renderer for the react-intl rich-text messages that carry the
// Enterprise contact mid-sentence. Shared because there are now two of them:
// the footnote under the ladder, and the "nothing to switch to" banner — which
// on a deployment that has retired every self-serve tier is the ONLY Enterprise
// route in the app, so it cannot be left as unlinked prose.
//
// `noopener` is the load-bearing half: without it the opened page gets a handle
// on this one through `window.opener` and can navigate it elsewhere.
const enterpriseLink = (chunks: React.ReactNode) => (
  <a href={ENTERPRISE_CONTACT_URL} target="_blank" rel="noopener noreferrer">
    {chunks}
  </a>
);

// Codes worth a row in the folded comparison matrix (the gated ladder — the
// always-on AI ops would be a row of ticks, which says nothing).
const MATRIX_FEATURE_CODES = [
  // First so it reads right under the share-pages allowance row.
  'SHARE_ANALYTICS',
  'CAMPAIGN_LINKS',
  'DELIVERY_INSIGHTS',
  'ANALYTICS_INTEGRATIONS',
  'AI_SCHEDULES',
  'FACE_RECOGNITION',
  'BYO_STORAGE',
  'DATABASE_EXPORT',
];

// Allowance rows, in the order a buyer weighs them. `storage_gb` is in GB;
// `cdn_bytes` is in BYTES (see the catalog's `unit`) — storage converts to
// bytes so both scale their own units (SCALE reads "2 TB", not "2,000 GB").
// 10^9 per GB, the decimal convention the backend meters in.
const QUOTA_ROWS: {
  dim: string;
  label: keyof typeof messages;
  format: (value: number) => string;
}[] = [
  { dim: 'users', label: 'compareContributors', format: (v) => formatNumberIntl(v) },
  { dim: 'viewers', label: 'compareViewers', format: (v) => formatNumberIntl(v) },
  { dim: 'storage_gb', label: 'compareStorage', format: (v) => formatBillingBytes(v * 1e9) },
  { dim: 'cdn_bytes', label: 'compareCdn', format: (v) => formatBillingBytes(v) },
  { dim: 'transcode_minutes', label: 'compareTranscode', format: (v) => `${formatNumberIntl(v)} min` },
  { dim: 'ai_credits', label: 'compareAiCredits', format: (v) => formatNumberIntl(v) },
  { dim: 'asset_schemas', label: 'compareSchemas', format: (v) => formatNumberIntl(v) },
  // Live-count entitlement caps (rollout 28) — capped, never billed; a 0
  // publishes with no rate, so quotaLabel renders "Unlimited" (Enterprise).
  { dim: 'collections', label: 'compareCollections', format: (v) => formatNumberIntl(v) },
  { dim: 'shares', label: 'compareShares', format: (v) => formatNumberIntl(v) },
  // Published only on plans that install PRINT_EXPORT, so a plan without it
  // reads "—" rather than its `included = 0` being taken for "Unlimited".
  // Exports past the allowance are priced in the credit costs list, not here.
  { dim: 'print_exports', label: 'comparePrintExports', format: (v) => formatNumberIntl(v) },
];

// Last-resort fallback for the schema add-on price — the catalog's
// `schema_price_usd` (from the backend's single source) always wins.
const SCHEMA_PRICE_FALLBACK_USD = 5;

// Static Enterprise column for the comparison matrix. CUSTOM plans are
// per-deal and deliberately absent from the self-serve catalog (no price, no
// published quotas), so these cells are curated marketing content, not
// catalog data — mirroring the enterprise pitch (committed-volume discounts
// on every meter, unlimited caps, the full feature set).
const ENTERPRISE_QUOTA_CELLS: Record<string, keyof typeof messages> = {
  users: 'compareCustom',
  viewers: 'unlimited',
  storage_gb: 'compareCommittedVolume',
  cdn_bytes: 'compareCommittedVolume',
  transcode_minutes: 'compareCommittedVolume',
  ai_credits: 'compareCommittedVolume',
  print_exports: 'unlimited',
  // Not "Unlimited" — Enterprise's schema pitch is the CAPABILITY (custom
  // schema authoring/customization), which no other tier has.
  asset_schemas: 'compareSchemaCustomizations',
  collections: 'unlimited',
  shares: 'unlimited',
};

// NB: rows that would be ✓ on EVERY plan belong in BASELINE_FEATURES (the
// prose "All plans include" line), not in the matrix — an all-tick row says
// nothing. Share analytics used to be the exception here; it is plan-gated
// from Starter up since rollout 32, so it rides MATRIX_FEATURE_CODES now.

// The subset that earns a line on a CARD (schemas and viewers stay
// matrix-only — five numbers is what a card can carry).
const CARD_DIMS = ['users', 'storage_gb', 'cdn_bytes', 'transcode_minutes', 'ai_credits'];

/**
 * How to render an allowance of ZERO — which means two opposite things:
 *
 *   0 with an overage rate  -> nothing is included; every unit is metered
 *                              from the first (this is PAYG).
 *   0 with no overage rate  -> genuinely unlimited.
 *
 * The rate decides it — never the allowance alone.
 */
const quotaLabel = (
  quota: TPlanQuota,
  format: (v: number) => string,
): [MessageDescriptor, Record<string, string>?] => {
  if (quota.included > 0) {
    return [messages.compareRawValue, { value: format(quota.included) }];
  }
  if (quota.overage_rate_usd) {
    // Metered from the first unit (PAYG): show what it costs.
    return quota.rate_unit
      ? [messages.compareRate, { price: String(quota.overage_rate_usd), unit: quota.rate_unit }]
      : [messages.compareMetered];
  }
  return [messages.unlimited];
};

// One tier card. Reports the card's differences and carries its own CTA (or
// the "Current plan" chip). `delta` is the gated features this tier adds over
// the one below it.
const PlanCard: React.FC<{
  plan: TCatalogPlan;
  isCurrent: boolean;
  previousName: string | null;
  delta: string[];
  cta: React.ReactNode;
}> = ({ plan, isCurrent, previousName, delta, cta }) => {
  const intl = useIntl();

  // A tier where every card dimension is metered (PAYG) collapses its
  // allowance list to one line — five rows of "Metered" say less than none.
  // Video is paid in credits on self-serve plans (conf/22): no minutes line.
  const cardQuotas = CARD_DIMS
    .filter((dim) => !(dim === 'transcode_minutes' && plan.transcode_billing === 'credits'))
    .map((dim) => ({ dim, quota: plan.quotas[dim] }))
    .filter((r): r is { dim: string; quota: TPlanQuota } => Boolean(r.quota));
  const allMetered = cardQuotas.length > 0 && cardQuotas.every(
    ({ quota }) => quota.included <= 0 && quota.overage_rate_usd,
  );

  return (
    <div className={cn(styles.pickerCard, { [styles.pickerCardCurrent]: isCurrent })}>
      <div className={styles.pickerCardHead}>
        <span className={styles.pickerCardName}>{plan.name}</span>
        {isCurrent && (
          <span className={styles.pickerCurrentTag}>
            <FormattedMessage {...messages.currentPlanTag} />
          </span>
        )}
      </div>
      <div className={styles.pickerPrice}>
        {/* A $0 fee with a monthly floor (PAYG) is "$5 min / month": not free,
            and not a fee on top — the rates listed below count toward it. */}
        {plan.price_usd === 0 && plan.monthly_minimum_usd ? (
          <FormattedMessage
            {...messages.comparePriceMinimum}
            values={{ price: formatNumberIntl(plan.monthly_minimum_usd) }}
          />
        ) : (
          <FormattedMessage
            {...messages.comparePricePerMonth}
            values={{ price: formatNumberIntl(plan.price_usd) }}
          />
        )}
      </div>

      {allMetered ? (
        <p className={styles.pickerAllowanceLine}>
          {plan.monthly_minimum_usd ? (
            <FormattedMessage
              {...messages.pickerMeteredWithMinimum}
              values={{ minimum: formatNumberIntl(plan.monthly_minimum_usd) }}
            />
          ) : (
            <FormattedMessage {...messages.pickerMetered} />
          )}
        </p>
      ) : (
        <ul className={styles.pickerAllowances}>
          {cardQuotas.map(({ dim, quota }) => {
            const row = QUOTA_ROWS.find((r) => r.dim === dim)!;
            return (
              <li key={dim}>
                <span>
                  <FormattedMessage {...messages[row.label]} />
                </span>
                <span className={styles.pickerAllowanceValue}>
                  {intl.formatMessage(...quotaLabel(quota, row.format))}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {previousName && (
        <p className={styles.pickerDelta}>
          {delta.length > 0 ? (
            <FormattedMessage
              {...messages.everythingInPlus}
              values={{
                plan: previousName,
                features: delta
                  .map((code) => intl.formatMessage(messages[FEATURE_LABELS[code]]))
                  .join(', '),
              }}
            />
          ) : (
            <FormattedMessage {...messages.everythingInMore} values={{ plan: previousName }} />
          )}
        </p>
      )}

      {cta && <div className={styles.pickerCta}>{cta}</div>}
    </div>
  );
};

// The folded tier×feature matrix — the full grid, for whoever wants it.
const CompareMatrix: React.FC<{
  plans: TCatalogPlan[];
  currentPlan: string;
  schemaPriceUsd?: number;
}> = ({ plans, currentPlan, schemaPriceUsd }) => {
  const intl = useIntl();

  const cell = (planCode: string, content: React.ReactNode) => (
    <td
      key={planCode}
      className={cn(styles.compareCell, planCode === currentPlan && styles.compareCurrent)}
    >
      {content}
    </td>
  );

  const quotaRows = QUOTA_ROWS.filter((row) => plans.some((p) => p.quotas[row.dim]));
  const featureCodes = MATRIX_FEATURE_CODES.filter((code) =>
    plans.some((p) => p.addons.includes(code)),
  );

  // The static Enterprise terminus — never the current plan (CUSTOM accounts
  // don't see this page at all), so a plain uncurrent cell.
  const enterpriseCell = (content: React.ReactNode) => (
    <td key="__ENTERPRISE__" className={cn(styles.compareCell, styles.compareEnterprise)}>
      {content}
    </td>
  );
  // A full-width section label row ("Usage per month", "Features").
  const groupRow = (key: string, label: MessageDescriptor) => (
    <tr key={`__group_${key}`} className={styles.compareGroup}>
      <th scope="colgroup" colSpan={plans.length + 2}>
        <FormattedMessage {...label} />
      </th>
    </tr>
  );
  const tick = (
    <span
      className={styles.compareYes}
      aria-label={intl.formatMessage(messages.compareIncluded)}
    >
      ✓
    </span>
  );

  return (
    <Table
      className={styles.compareTable}
      headers={[
        // The corner cell stays empty: the row labels below say what each
        // row is, and the section rows say what each block is.
        '',
        ...plans.map((p) => p.name),
        intl.formatMessage(messages.compareEnterpriseColumn),
      ]}
    >
      <tr>
        <th scope="row">
          <FormattedMessage {...messages.comparePrice} />
        </th>
        {plans.map((p) =>
          cell(
            p.code,
            <strong>
              {p.price_usd === 0 && p.monthly_minimum_usd ? (
                <FormattedMessage
                  {...messages.comparePriceMinimum}
                  values={{ price: formatNumberIntl(p.monthly_minimum_usd) }}
                />
              ) : (
                <FormattedMessage
                  {...messages.comparePricePerMonth}
                  values={{ price: formatNumberIntl(p.price_usd) }}
                />
              )}
            </strong>,
          ),
        )}
        {enterpriseCell(
          <strong>
            <a href={ENTERPRISE_CONTACT_URL} target="_blank" rel="noopener noreferrer">
              <FormattedMessage {...messages.compareContactUs} />
            </a>
          </strong>,
        )}
      </tr>

      {quotaRows.length > 0 && groupRow('usage', messages.compareUsageGroup)}
      {quotaRows.map((row) => (
        <tr key={row.dim}>
          <th scope="row">
            <FormattedMessage {...messages[row.label]} />
          </th>
          {plans.map((p) => {
            const quota = p.quotas[row.dim];
            if (!quota) return cell(p.code, '—');
            // Derived viewer rule (migration 53): the catalog can't resolve
            // "10 × contributor seats" to a number for an arbitrary account,
            // so it publishes the multiplier and we say so.
            if (row.dim === 'viewers' && quota.per_seat) {
              return cell(p.code, intl.formatMessage(messages.comparePerSeat, {
                count: formatNumberIntl(quota.per_seat),
              }));
            }
            // conf/22: video on a self-serve plan is paid in credits per
            // minute; its minutes allowance is 0 with no rate, which
            // quotaLabel would otherwise read as "Unlimited".
            if (row.dim === 'transcode_minutes' && p.transcode_billing === 'credits') {
              return cell(p.code, intl.formatMessage(messages.compareVideoInCredits));
            }
            // Schema add-on (migration 54): the included figure alone would
            // undersell the plan — it can buy up to max_total.
            if (row.dim === 'asset_schemas' && p.schema_addon) {
              return cell(p.code, intl.formatMessage(messages.compareSchemasAddon, {
                included: formatNumberIntl(quota.included),
                price: formatNumberIntl(schemaPriceUsd ?? SCHEMA_PRICE_FALLBACK_USD),
                max: formatNumberIntl(p.schema_addon.max_total),
              }));
            }
            return cell(p.code, intl.formatMessage(
              ...quotaLabel(quota, row.format),
            ));
          })}
          {enterpriseCell(
            ENTERPRISE_QUOTA_CELLS[row.dim] ? (
              <FormattedMessage {...messages[ENTERPRISE_QUOTA_CELLS[row.dim]]} />
            ) : (
              '—'
            ),
          )}
        </tr>
      ))}

      {featureCodes.length > 0 && groupRow('features', messages.compareFeaturesGroup)}
      {featureCodes.map((code) => (
        <tr key={code}>
          <th scope="row">
            <FormattedMessage {...messages[FEATURE_LABELS[code]]} />
          </th>
          {plans.map((p) =>
            cell(
              p.code,
              p.addons.includes(code) ? tick : (
                <span className={styles.compareNo} aria-label={intl.formatMessage(messages.compareNotIncluded)}>
                  —
                </span>
              ),
            ),
          )}
          {enterpriseCell(tick)}
        </tr>
      ))}
    </Table>
  );
};

const PickerContent: React.FC<{
  subscription: TSubscription;
  plans: TCatalogPlan[];
  isOwner: boolean;
  schemaPriceUsd?: number;
  // The account's own plan when this deployment has retired it — the
  // grandfathered case. Null normally.
  retiredCurrentPlan?: TCatalogPlan | null;
}> = ({ subscription, plans, isOwner, schemaPriceUsd, retiredCurrentPlan }) => {
  const intl = useIntl();
  const history = useHistory();
  const { showModal } = useModal();
  const checkout = useStripeCheckout();

  const status = subscription.status;
  const onTrial = subscription.details.is_trial || status === 'trialing';
  // No chargeable Stripe subscription behind the account: a live trial, or a
  // trial that ended without a card (`payment_required`, card-less PAYG). An
  // in-place change has nothing to modify there — the server refuses it with
  // "A payment method is required" — so a plan is bought through Checkout,
  // whose completion activates it (`activate_paid_plan`). Found on dev
  // 2026-10-17: PAYG -> Growth from `payment_required` 400'd every time.
  const needsCheckout = onTrial || status === 'payment_required';
  const pendingPlan = subscription.pending_plan;

  // Arm the purchase-toast baseline the Subscription page reads on mount, so
  // an upgrade bought HERE still toasts "You're now on the X plan" THERE.
  const startCheckout = (planCode: string) => {
    try {
      sessionStorage.setItem(
        BILLING_BASELINE_KEY,
        JSON.stringify({
          balance: subscription.usage?.ai_credits?.balance ?? 0,
          plan: subscription.plan,
          ts: Date.now(),
        }),
      );
    } catch {
      /* sessionStorage unavailable — the plan still flips, only the toast is lost */
    }
    return checkout.startCheckout(planCode);
  };

  // What every purchasable tier shares, stated ONCE — cards then carry only
  // differences. The entitlement half is the intersection of the tiers' addon
  // sets; the baseline half is the product itself, which has no addon codes to
  // intersect. Baseline first: "DAM + CDN + AI Agent" is what the product IS,
  // and the AI operations that follow are what it does.
  const sharedCodes = Object.keys(FEATURE_LABELS).filter((code) =>
    plans.every((p) => p.addons.includes(code)),
  );
  const sharedFeatures = [
    ...BASELINE_FEATURES.map((key) => intl.formatMessage(messages[key])),
    ...sharedCodes.map((code) => intl.formatMessage(messages[FEATURE_LABELS[code]])),
  ];

  // Whether this account may act on a card at all. A pending change parks the
  // CTAs: the scheduled switch is managed (kept or reversed) from the
  // Subscription page, and stacking a second change on top from here is how
  // customers end up surprised at renewal.
  const canAct = isOwner && !pendingPlan && status !== 'cancelled';

  const ctaFor = (plan: TCatalogPlan): React.ReactNode => {
    if (!canAct || plan.code === subscription.plan) return null;
    if (needsCheckout) {
      if (!checkout.enabled) return null;
      return (
        <Button
          mode="primary"
          size="small"
          onClick={() => startCheckout(plan.code)}
          disabled={checkout.isLoading}
        >
          <FormattedMessage {...messages.pickerUpgradeCta} />
        </Button>
      );
    }
    // `plans` holds only what is offered, so an account on a retired tier
    // (Team, Starter) isn't in it — fall back to the retired plan, or every
    // card, cheaper ones included, would read "Upgrade".
    const current = plans.find((p) => p.code === subscription.plan) ?? retiredCurrentPlan;
    const upgrade = !current || plan.price_usd > current.price_usd;
    return (
      <Button
        mode={upgrade ? 'primary' : 'secondary'}
        size="small"
        onClick={() => showModal(<ChangePlanModal targetCode={plan.code} />)}
      >
        <FormattedMessage
          {...(upgrade ? messages.pickerUpgradeCta : messages.pickerSwitchCta)}
        />
      </Button>
    );
  };

  return (
    <>
      {/* Embedded checkout for the trial's direct purchases. On completion,
          return to the Subscription page — the new plan (and the armed toast)
          live there. The 2.6s delay lets Stripe's success state show first,
          matching `handleComplete`'s own close timing. */}
      <CheckoutModal
        clientSecret={checkout.clientSecret}
        onComplete={() => {
          checkout.handleComplete();
          window.setTimeout(() => history.push(SUBSCRIPTION_ROUTE), 2600);
        }}
        onClose={checkout.closeCheckout}
      />

      {pendingPlan && (
        <div className={styles.pendingChange} role="status">
          <FormattedMessage
            {...messages.pendingChangeBannerNoDate}
            values={{ plan: subscription.pending_plan_name ?? pendingPlan }}
          />
        </div>
      )}
      {!isOwner && (
        <p className={styles.planFooter}>
          <FormattedMessage
            {...messages.planChangesNonOwner}
            values={{ email: subscription.owner_email }}
          />
        </p>
      )}
      {onTrial && canAct && (
        <p className={styles.pickerIntro}>
          <FormattedMessage {...messages.pickerTrialIntro} />
        </p>
      )}

      {/* Grandfathered: the plan this account is on is no longer offered, so
          it has no card below. Without this line the reader sees a page of
          plans, none of them marked as theirs, and has to work out whether
          their plan still exists — the one thing grandfathering is supposed
          to reassure them about. */}
      {retiredCurrentPlan && (
        <p className={styles.pickerIntro} role="status">
          <FormattedMessage
            {...messages.pickerRetiredCurrentPlan}
            values={{ plan: retiredCurrentPlan.name }}
          />
        </p>
      )}

      {sharedFeatures.length > 0 && (
        <p className={styles.pickerShared}>
          <span className={styles.pickerSharedLabel}>
            <FormattedMessage {...messages.allPlansInclude} />
          </span>{' '}
          {sharedFeatures.join(' · ')}
        </p>
      )}

      <div className={styles.pickerGrid}>
        {plans.map((plan, i) => {
          const prev = i > 0 ? plans[i - 1] : null;
          const delta = prev
            ? plan.addons.filter(
                (code) => FEATURE_LABELS[code]
                  && !sharedCodes.includes(code)
                  && !prev.addons.includes(code),
              )
            : [];
          return (
            <PlanCard
              key={plan.code}
              plan={plan}
              isCurrent={plan.code === subscription.plan}
              previousName={prev ? prev.name : null}
              delta={delta}
              cta={ctaFor(plan)}
            />
          );
        })}
      </div>

      {checkout.isLoading && (
        <p className={styles.upgradeIntro} role="status">
          <FormattedMessage {...messages.upgradeStarting} />
        </p>
      )}
      {checkout.error && (
        <p className={styles.checkoutError} role="alert">
          <FormattedMessage {...messages.checkoutError} />
        </p>
      )}

      {/* Small print about the tiers above: what overage costs, and that seats
          are addable to any of them. */}
      <p className={styles.billingCardCaption}>
        <FormattedMessage {...messages.compareFootnote} />
      </p>

      {/* The Enterprise escape hatch, deliberately NOT small print. It used to
          be the tail of the sentence above, at 50% opacity — which made the one
          line addressed to customers the tiers DON'T fit the least visible
          thing on the page. */}
      <p className={styles.enterpriseNote}>
        <FormattedMessage
          {...messages.enterpriseFootnote}
          values={{ link: enterpriseLink }}
        />
      </p>

      {/* Always shown, under its own section heading — a folded toggle left
          "Compare plans" stranded on a line by itself. */}
      <AdminSettingsSection title={intl.formatMessage(messages.compareAllFeatures)}>
        <CompareMatrix
          plans={plans}
          currentPlan={subscription.plan}
          schemaPriceUsd={schemaPriceUsd}
        />
      </AdminSettingsSection>
    </>
  );
};

export const PlanPicker: React.FC = () => {
  const intl = useIntl();
  const auth = useAuthUser();
  const currentUserId = auth()?.userId;
  const { data: subscription, isLoading: subLoading } = useSubscriptionQuery();
  const { data: catalog, isLoading: catLoading } = usePlansQuery({ retry: false });

  const renderShell = (body: React.ReactNode) => (
    <div className={styles.pageWrapper}>
      <PageTitle
        className={styles.pageTitle}
        title={intl.formatMessage(messages.pickerTitle)}
      />
      <PageBody>
        <p className={styles.pickerBack}>
          <Link to={SUBSCRIPTION_ROUTE}>
            ← <FormattedMessage {...messages.pageTitle} />
          </Link>
        </p>
        {body}
      </PageBody>
    </div>
  );

  if (subLoading || catLoading) {
    return renderShell(
      <div className={styles.loadingContainer}>
        <Loader />
      </div>,
    );
  }

  // The catalog as delivered, minus trial tiers and minus anything this
  // deployment has retired (REACT_APP_HIDDEN_PLAN_CODES — see planVisibility).
  const plans = offeredPlans(catalog?.plans);
  const catalogPlans = (catalog?.plans ?? []).filter((p) => !p.is_trial);

  // "The catalog didn't load" and "we offer nothing right now" look the same
  // from here but are not the same message: blaming the catalog for a
  // deliberate configuration sends the reader to support with a bug report.
  if (!subscription || catalogPlans.length === 0) {
    return renderShell(
      <div className={styles.errorBanner}>
        <FormattedMessage {...messages.loadError} />
      </div>,
    );
  }

  // Enterprise / negotiated plans are invoiced against a contract — there is
  // nothing self-serve to pick. Say so rather than offering plans that a
  // checkout would refuse.
  if (!isSelfServePlan(subscription)) {
    return renderShell(
      <div className={styles.infoBanner}>
        <FormattedMessage {...messages.paymentMethodEnterprise} />
      </div>,
    );
  }

  // Every tier retired at once — the shape of a deployment that has paused
  // self-serve entirely (a pricing review, say). Reachable by configuration, so
  // it needs an answer: the account keeps what it has, and Enterprise is the
  // one route onward. `PickerContent` is skipped here, and it owns the only
  // other Enterprise link on the page, so this message carries its own.
  if (plans.length === 0) {
    return renderShell(
      <div className={styles.infoBanner}>
        <FormattedMessage
          {...messages.pickerNoPlansOffered}
          values={{ link: enterpriseLink }}
        />
      </div>,
    );
  }

  const isOwner =
    currentUserId !== undefined && Number(currentUserId) === subscription.owner_user_id;

  return renderShell(
    <PickerContent
      subscription={subscription}
      plans={plans}
      isOwner={isOwner}
      schemaPriceUsd={catalog?.schema_price_usd}
      retiredCurrentPlan={hiddenCurrentPlan(catalog?.plans, subscription.plan)}
    />,
  );
};

export default PlanPicker;
